import { Center, Loader } from "@mantine/core";
import { safeApiCall } from "api/common.service";
import {
  getSalesAnalysisByMonth,
  getSalesAnalysisByQuarter,
} from "api/report.service";
import { MAX_PAGE_SIZE } from "common/constants";
import {
  calculateSalesMetrics,
  formatNumberAsString,
  safeUnits,
} from "common/utils";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  RprtSalesAnalysisByQuarters,
  RptSalesAnalysisByMonths,
} from "types/model.types";

type SalesData = RptSalesAnalysisByMonths | RprtSalesAnalysisByQuarters;
const SalesAnalysisPreview: React.FC = () => {
  const [data, setData] = useState<SalesData>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [params] = useSearchParams();
  const startCode = params.get("f");
  const endCode = params.get("t");
  const rptType = params.get("rpt") ?? 0;
  const reportType = rptType ? Number(rptType ?? 0) : 0;
  const selectedYear = params.get("y") ?? new Date().getFullYear();

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        const from: number | null = startCode ? Number(startCode) : null;
        const to: number | null = endCode ? Number(endCode) : null;

        let result;
        switch (reportType) {
          case 0:
            result = await safeApiCall(
              getSalesAnalysisByMonth(
                Number(selectedYear),
                from,
                to,
                MAX_PAGE_SIZE,
                1,
              ),
            );
            break;
          default:
            result = await safeApiCall(
              getSalesAnalysisByQuarter(
                Number(selectedYear),
                from,
                to,
                MAX_PAGE_SIZE,
                1,
              ),
            );
            break;
        }
        if (mounted) setData(result?.data ?? []);
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  // our datatypes consist of 2 structure types... we need to
  // safe guard it
  //Monthly rows always have jan.
  function isMonthly(rows: SalesData): rows is RptSalesAnalysisByMonths {
    return rows.length > 0 && "jan" in rows[0];
  }
  //Quarterly rows always have qtr1.
  function isQuarterly(rows: SalesData): rows is RprtSalesAnalysisByQuarters {
    return rows.length > 0 && "qtr1" in rows[0];
  }

  let rowsToRender = null;

  if (isMonthly(data)) {
    rowsToRender = data.map((row, index) => {
      const units = safeUnits(row.unitsCtn);
      return (
        <tr key={`${index}-${row.id}`}>
          <td>{row.id}</td>
          <td className="description xs">{row.description}</td>
          <td>{formatNumberAsString(row.lastSale / units)}</td>
          <td>{formatNumberAsString(row.jan / units)}</td>
          <td>{formatNumberAsString(row.feb / units)}</td>
          <td>{formatNumberAsString(row.mar / units)}</td>
          <td>{formatNumberAsString(row.apr / units)}</td>
          <td>{formatNumberAsString(row.may / units)}</td>
          <td>{formatNumberAsString(row.jun / units)}</td>
          <td>{formatNumberAsString(row.jul / units)}</td>
          <td>{formatNumberAsString(row.aug / units)}</td>
          <td>{formatNumberAsString(row.sep / units)}</td>
          <td>{formatNumberAsString(row.oct / units)}</td>
          <td>{formatNumberAsString(row.nov / units)}</td>
          <td>{formatNumberAsString(row.dec / units)}</td>
          <td>{formatNumberAsString(row.cord / units)}</td>
          <td>{formatNumberAsString(row.sord / units)}</td>
          <td>{formatNumberAsString(row.onHand / units)}</td>
          <td>{row.onHand <= 0 ? " <<< " : ""}</td>
        </tr>
      );
    });
  }

  if (isQuarterly(data)) {
    rowsToRender = data.map((row, index) => {
      const { avg, exAvg, ams, months } = calculateSalesMetrics(row);
      const units = safeUnits(row.unitsCtn);
      return (
        <tr key={`${index}-${row.id}`}>
          <td>{row.id}</td>
          <td className="description xs">{row.description}</td>
          <td>{formatNumberAsString(row.lastSale / units)}</td>
          <td>{formatNumberAsString(row.qtr1 / units)}</td>
          <td>{formatNumberAsString(row.qtr2 / units)}</td>
          <td>{formatNumberAsString(row.qtr3 / units)}</td>
          <td>{formatNumberAsString(row.qtr4 / units)}</td>
          <td>{formatNumberAsString(row.total / units)}</td>
          <td>{formatNumberAsString(avg)}</td>
          <td>{formatNumberAsString(exAvg)}</td>
          <td>{formatNumberAsString(ams)}</td>
          <td>{formatNumberAsString(row.cord / units)}</td>
          <td>{formatNumberAsString(row.sord / units)}</td>
          <td>{formatNumberAsString(row.onHand / units)}</td>
          <td>{months}</td>
          <td>{row.onHand <= 0 ? " <<< " : ""}</td>
        </tr>
      );
    });
  }
  if (loading) {
    return (
      <Center style={{ height: "100vh" }}>
        <Loader size="lg" />
      </Center>
    );
  }
  return (
    <>
      <div className="sub-title">
        {reportType === 0
          ? "Product Sales Analysis Report for "
          : "Products Monthly Averages Report for "}
        {selectedYear}
      </div>
      <table className="details-table">
        <thead>
          <tr>
            <th>Product</th>
            <th className="description xs">Description</th>
            <th>Last</th>
            {reportType !== 0 ? (
              <>
                <th>Qtr 1</th>
                <th>Qtr 2</th>
                <th>Qtr 3</th>
                <th>Qtr 4</th>
                <th>Total</th>
                <th>Avg</th>
                <th>Ex-Avg</th>
                <th>Avg. M/S</th>
                <th>C/Ord</th>
                <th>S/Ord</th>
                <th>On Hand</th>
                <th>Months</th>
                <th>Order</th>
              </>
            ) : (
              <>
                <th>Jan</th>
                <th>Feb</th>
                <th>Mar</th>
                <th>Apr</th>
                <th>May</th>
                <th>Jun</th>
                <th>Jul</th>
                <th>Aug</th>
                <th>Sep</th>
                <th>Oct</th>
                <th>Nov</th>
                <th>Dec</th>
                <th>C/Ord</th>
                <th>S/Ord</th>
                <th className="break">On Hand</th>
                <th>Order</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>{rowsToRender}</tbody>
      </table>
    </>
  );
};

export default SalesAnalysisPreview;
